/**
 * Created by robert.lavoie on 7/13/17.
 */

import { Injectable, Inject, PLATFORM_ID  } from '@angular/core';
import { Observable } from 'rxjs';
import * as Raven from 'raven-js';
import { isPlatformBrowser } from '@angular/common';
import { take } from 'rxjs/operators';

const GEOLOCATION_ERRORS = {
  'errors.location.unsupportedBrowser': 'Browser does not support location services',
  'errors.location.permissionDenied': 'You have rejected access to your location',
  'errors.location.positionUnavailable': 'Unable to determine your location',
  'errors.location.timeout': 'Service timeout has been reached',
  'errors.permissions.api.unsupported': 'Permission API is not supported',
  'errors.permissions.api.notGranted': 'Geolocation Permissions not granted'
};


export interface IGeoOptions {
  enableHighAccuracy: boolean;
  timeout: number;
  maximumAge: number;
}


/**
 *
 * @class
 * GeoLocationService
 *
 * @description
 * Obtains the geographic position, in terms of latitude and longitude coordinates, of the device.
 *
 */
@Injectable()
export class GeoLocationService {

  // Default geo Location api options
  private defaultOptions: IGeoOptions = {
    enableHighAccuracy: false,
    timeout: 10000,
    maximumAge: 75000
  };


  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}



  /**
   *
   * Obtains the geographic position, in terms of latitude and longitude coordinates, of the device.
   *
   * @param {Object} [opts] An object literal to specify one or more of the following attributes and desired values:
   *   - enableHighAccuracy: Specify true to obtain the most accurate position possible, or false to optimize in favor of performance and power consumption.
   *   - timeout: An Integer value that indicates the time, in milliseconds, allowed for obtaining the position.
   *              If timeout is Infinity, (the default value) the location request will not time out.
   *              If timeout is zero (0) or negative, the results depend on the behavior of the location provider.
   *   - maximumAge: An Integer value indicating the maximum age, in milliseconds, of cached position information.
   *                 If maximumAge is non-zero, and a cached position that is no older than maximumAge is available, the cached position is used instead of obtaining an updated location.
   *                 If maximumAge is zero (0), watchPosition always tries to obtain an updated position, even if a cached position is already available.
   *                 If maximumAge is Infinity, any cached position is used, regardless of its age, and watchPosition only tries to obtain an updated position if no cached position data exists.
   *
   * @returns {Observable} A Observable with the geographical location of the device running the client.
   */
  public getLocation(opts: IGeoOptions = this.defaultOptions): Observable<any> {


    return Observable.create((observer) => {

      if (window.navigator && window.navigator.geolocation) {

        window.navigator.geolocation.getCurrentPosition(
          (position) => {
            observer.next(position);
          },
          (error) => {
            switch (error.code) {
              case 1:
                observer.error(GEOLOCATION_ERRORS['errors.location.permissionDenied']);

                // Track when a user denied permissions
                Raven.captureMessage(GEOLOCATION_ERRORS['errors.location.permissionDenied'], {
                  level: 'info'
                });

                break;
              case 2:

                // Track geo location position unavailable
                Raven.captureMessage(GEOLOCATION_ERRORS['errors.location.positionUnavailable'], {
                  level: 'info'
                });

                observer.error(GEOLOCATION_ERRORS['errors.location.positionUnavailable']);
                break;
              case 3:

                // Track geo location timeouts
                Raven.captureMessage(GEOLOCATION_ERRORS['errors.location.timeout'], {
                  level: 'info'
                });

                observer.error(GEOLOCATION_ERRORS['errors.location.timeout']);
                break;
            }
          },
          opts);

      }else{
        observer.error(GEOLOCATION_ERRORS['errors.location.unsupportedBrowser']);
      }

    }).pipe(take(1));

  }

  /**
   * Uses the permissions api to see is the user had previously given location permissions
   * This is to prevent using the prompt dialog on page load where we can fallback to IP
   *
   * @param { Observable } A Observable with geolocation permissions state
   */

  public getLocationPermissions(): Observable<any> {

    return Observable.create((observer) => {

      // Make sure the Permission API is implemented
      if (window.navigator['permissions']){

        navigator['permissions'].query({ name: 'geolocation' }).then((permission) => {


          // The user has granted location permissions so lets use that instead of IP
          if (permission.state === 'granted'){

            observer.next(permission.state);

          }else{
            observer.error(GEOLOCATION_ERRORS['errors.permissions.api.notGranted']);

          }


        });


      }else{
        // browser does not support the permissions api
        observer.error(GEOLOCATION_ERRORS['errors.permissions.api.unsupported']);
      }


    }).pipe(take(1));

  }


  /**
   * Used to define wether a users browser has geolocation capabilities
   *
   * @param { boolean }
   */
  public hasGeoCapabilities(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return window.navigator && typeof window.navigator.geolocation != undefined;
    }
  }
}